import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { AccountDetailBusinessHours } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/loaders/use-account-details-loader';
import { AccountDetailHoursPill } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/components/account-detail-hours-pill';
import { createStyles } from '@/contexts/theme-context';
import { DAYS_OF_WEEK } from '@/utils/date';
import { formatHoursRange } from '@/utils/business-hours';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const ACCOUNT_HOURS_DROPDOWN_WIDTH = 248;

export const AccountDetailHoursDropdown: React.FC<
  AccountDetailBusinessHours
> = ({ dayToHoursMap, isCurrentlyOpen, nextOpenSlot, orderedDays }) => {
  const [styles, theme] = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  if (orderedDays.every((day) => !dayToHoursMap[day])) {
    return null;
  }

  return (
    <View style={styles.container}>
      <AccountDetailHoursPill
        isCurrentlyOpen={isCurrentlyOpen}
        isExpanded={isOpen}
        nextOpenSlot={nextOpenSlot}
        onPress={toggleDropdown}
      />
      {isOpen ? (
        <View style={styles.weeklyScheduleCard}>
          {orderedDays.map((day) => {
            const dayHours = dayToHoursMap[day];

            return (
              <View key={day} style={styles.dayRow}>
                <View style={styles.dayNameCell}>
                  <Typography variant="paragraph-md-600">
                    {i18n.t(DAYS_OF_WEEK[day])}
                  </Typography>
                </View>
                <View style={styles.hoursCell}>
                  {dayHours ? (
                    dayHours.map((entry, index) => (
                      <Typography
                        color={theme.sk.color.text.primary}
                        key={index}
                        textAlign="right"
                        variant="paragraph-md"
                      >
                        {formatHoursRange(entry)}
                      </Typography>
                    ))
                  ) : (
                    <Typography
                      color={theme.sk.color.text.primary}
                      textAlign="right"
                      variant="paragraph-md"
                    >
                      {i18n.t('closed')}
                    </Typography>
                  )}
                </View>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};

const DROPDOWN_SHADOW = shadow({
  opacity: 0.08,
  radius: 24,
  x: 0,
  y: 12,
});

const useStyles = createStyles((theme) => ({
  container: {
    zIndex: 1,
  },
  dayNameCell: {
    flex: 1,
    height: theme.sk.spacing.spaceLarge,
    justifyContent: 'center',
  },
  dayRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
  hoursCell: {
    alignItems: 'flex-end',
    flex: 1,
    justifyContent: 'center',
    minHeight: theme.sk.spacing.spaceLarge,
  },
  weeklyScheduleCard: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    gap: theme.sk.spacing.spaceXsmall,
    left: 0,
    marginTop: theme.sk.spacing.spaceXsmall,
    minWidth: ACCOUNT_HOURS_DROPDOWN_WIDTH,
    padding: theme.sk.spacing.spaceSMedium,
    position: 'absolute',
    top: '100%',
    ...DROPDOWN_SHADOW,
  },
}));
